var __assign = (this && this.__assign) || function () {
    __assign = Object.assign || function(t) {
        for (var s, i = 1, n = arguments.length; i < n; i++) {
            s = arguments[i];
            for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p))
                t[p] = s[p];
        }
        return t;
    };
    return __assign.apply(this, arguments);
};
var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
var __generator = (this && this.__generator) || function (thisArg, body) {
    var _ = { label: 0, sent: function() { if (t[0] & 1) throw t[1]; return t[1]; }, trys: [], ops: [] }, f, y, t, g = Object.create((typeof Iterator === "function" ? Iterator : Object).prototype);
    return g.next = verb(0), g["throw"] = verb(1), g["return"] = verb(2), typeof Symbol === "function" && (g[Symbol.iterator] = function() { return this; }), g;
    function verb(n) { return function (v) { return step([n, v]); }; }
    function step(op) {
        if (f) throw new TypeError("Generator is already executing.");
        while (g && (g = 0, op[0] && (_ = 0)), _) try {
            if (f = 1, y && (t = op[0] & 2 ? y["return"] : op[0] ? y["throw"] || ((t = y["return"]) && t.call(y), 0) : y.next) && !(t = t.call(y, op[1])).done) return t;
            if (y = 0, t) op = [op[0] & 2, t.value];
            switch (op[0]) {
                case 0: case 1: t = op; break;
                case 4: _.label++; return { value: op[1], done: false };
                case 5: _.label++; y = op[1]; op = [0]; continue;
                case 7: op = _.ops.pop(); _.trys.pop(); continue;
                default:
                    if (!(t = _.trys, t = t.length > 0 && t[t.length - 1]) && (op[0] === 6 || op[0] === 2)) { _ = 0; continue; }
                    if (op[0] === 3 && (!t || (op[1] > t[0] && op[1] < t[3]))) { _.label = op[1]; break; }
                    if (op[0] === 6 && _.label < t[1]) { _.label = t[1]; t = op; break; }
                    if (t && _.label < t[2]) { _.label = t[2]; _.ops.push(op); break; }
                    if (t[2]) _.ops.pop();
                    _.trys.pop(); continue;
            }
            op = body.call(thisArg, _);
        } catch (e) { op = [6, e]; y = 0; } finally { f = t = 0; }
        if (op[0] & 5) throw op[1]; return { value: op[0] ? op[1] : void 0, done: true };
    }
};
/**
 * CZNA-1409 — walks the cataloguer through the back-link offers a save returned, one modal per
 * offer, and resolves once every offer has been confirmed or declined. A save path awaits it
 * before it navigates away, so the offer is answered while the saved record is still on screen.
 *
 * A confirmed offer is a change proposal of the target, created from `backLink` (the server
 * builds its data) and submitted for review straight away. A submit that fails after the draft
 * landed is retried on the same draft, never by creating a second one — and declined after
 * that, the draft is deleted, because a declined offer creates nothing.
 */
import React, { useCallback, useRef, useState } from 'react';
import { useIntl } from 'react-intl';
import { useQueryClient } from 'react-query';
import { useOkapiKy } from '@folio/stripes/core';
import { BackLinkOfferModal } from './BackLinkOfferModal';
import { createProposal, deleteProposal, submitProposal } from '../../api/proposalService';
import { formatSaveError } from '../../utils/formatSaveError';
import { isTimeoutError } from '../../utils/errorKind';
import { PROPOSALS_LIST_HREF, saveOutcomeUnknownError, } from '../../utils/saveOutcomeUnknown';
import { PROPOSALS_QUERY_ROOT } from '../../hooks/useProposalList';
import { ProposalType } from '../../types/api';
export var useBackLinkOffers = function () {
    var intl = useIntl();
    var ky = useOkapiKy();
    var queryClient = useQueryClient();
    var _a = useState(null), queue = _a[0], setQueue = _a[1];
    var _b = useState(false), inFlight = _b[0], setInFlight = _b[1];
    var _c = useState(null), error = _c[0], setError = _c[1];
    /**
     * CZNA-1625 — targets whose confirm went unanswered (create or submit timed out). Their
     * back-link proposal may exist, so they cannot be confirmed again — that could create a
     * second one — and declining them deletes nothing: a draft we cannot see the state of is
     * not ours to remove.
     */
    var _d = useState({}), unknownTargets = _d[0], setUnknownTargets = _d[1];
    var resolveRef = useRef(null);
    /** The draft a failed submit left behind, keyed by target — retried, not re-created. */
    var draftsRef = useRef({});
    var offerBackLinks = useCallback(function (offers) {
        if (!offers || offers.length === 0)
            return Promise.resolve();
        draftsRef.current = {};
        setUnknownTargets({});
        return new Promise(function (resolve) {
            resolveRef.current = resolve;
            setError(null);
            setQueue({ offers: offers, index: 0 });
        });
    }, []);
    var advance = function () {
        setError(null);
        setQueue(function (current) {
            if (current && current.index + 1 < current.offers.length) {
                return __assign(__assign({}, current), { index: current.index + 1 });
            }
            var resolve = resolveRef.current;
            resolveRef.current = null;
            if (resolve)
                setTimeout(resolve, 0);
            return null;
        });
    };
    var confirm = function (offer, designation) { return __awaiter(void 0, void 0, void 0, function () {
        var draftId, created, e_1, _a, _b, _c, _d;
        var _e;
        return __generator(this, function (_f) {
            switch (_f.label) {
                case 0:
                    setInFlight(true);
                    setError(null);
                    _f.label = 1;
                case 1:
                    _f.trys.push([1, 5, 7, 8]);
                    draftId = draftsRef.current[offer.targetRecordId];
                    if (!!draftId) return [3 /*break*/, 3];
                    return [4 /*yield*/, createProposal(ky, {
                            type: offer.targetType,
                            proposalType: ProposalType.UPDATE_EXISTING,
                            targetRecordId: offer.targetRecordId,
                            backLink: {
                                sourceRecordId: offer.sourceRecordId,
                                control: designation ? null : offer.backControl,
                                designation: designation,
                            },
                            proposal_data: [], // eslint-disable-line camelcase
                        })];
                case 2:
                    created = _f.sent();
                    draftId = created.id;
                    draftsRef.current[offer.targetRecordId] = draftId;
                    _f.label = 3;
                case 3: return [4 /*yield*/, submitProposal(ky, draftId)];
                case 4:
                    _f.sent();
                    queryClient.invalidateQueries([PROPOSALS_QUERY_ROOT]);
                    advance();
                    return [3 /*break*/, 8];
                case 5:
                    e_1 = _f.sent();
                    // CZNA-1625 — a create or submit nobody answered: the back-link proposal may exist. Say
                    // so, point at the proposal list, and take this offer's Confirm away — repeating it after
                    // a create that did land would make a duplicate (the draft id never came back).
                    if (isTimeoutError(e_1)) {
                        setUnknownTargets(function (current) {
                            var _a;
                            return (__assign(__assign({}, current), (_a = {}, _a[offer.targetRecordId] = true, _a)));
                        });
                        setError(saveOutcomeUnknownError(intl.formatMessage({ id: 'ui-czna-authorities.backLinkOffer.timeout' }), {
                            kind: 'checkList',
                            href: PROPOSALS_LIST_HREF,
                            linkLabelId: 'ui-czna-authorities.proposalForm.createTimeout.openList',
                        }));
                        return [2 /*return*/];
                    }
                    _a = setError;
                    _c = (_b = intl).formatMessage;
                    _d = [{ id: 'ui-czna-authorities.backLinkOffer.error' }];
                    _e = {};
                    return [4 /*yield*/, formatSaveError(e_1, intl)];
                case 6:
                    _a.apply(void 0, [_c.apply(_b, _d.concat([(_e.message = _f.sent(), _e)]))]);
                    return [3 /*break*/, 8];
                case 7:
                    setInFlight(false);
                    return [7 /*endfinally*/];
                case 8: return [2 /*return*/];
            }
        });
    }); };
    var decline = function (offer) { return __awaiter(void 0, void 0, void 0, function () {
        var draftId, e_2, _a, _b, _c, _d, _e;
        var _f;
        return __generator(this, function (_g) {
            switch (_g.label) {
                case 0:
                    draftId = draftsRef.current[offer.targetRecordId];
                    // CZNA-1625 — outcome unknown: skip it, never delete what may have been submitted.
                    if (unknownTargets[offer.targetRecordId]) {
                        delete draftsRef.current[offer.targetRecordId];
                        advance();
                        return [2 /*return*/];
                    }
                    if (!draftId) return [3 /*break*/, 8];
                    setInFlight(true);
                    _g.label = 1;
                case 1:
                    _g.trys.push([1, 3, 7, 8]);
                    return [4 /*yield*/, deleteProposal(ky, draftId)];
                case 2:
                    _g.sent();
                    delete draftsRef.current[offer.targetRecordId];
                    return [3 /*break*/, 8];
                case 3:
                    e_2 = _g.sent();
                    // CZNA-1625 — the draft's delete may have happened; see `accept`.
                    _a = setError;
                    if (!isTimeoutError(e_2)) return [3 /*break*/, 4];
                    _b = intl.formatMessage({ id: 'ui-czna-authorities.error.timeout' });
                    return [3 /*break*/, 6];
                case 4:
                    _d = (_c = intl).formatMessage;
                    _e = [{ id: 'ui-czna-authorities.backLinkOffer.error' }];
                    _f = {};
                    return [4 /*yield*/, formatSaveError(e_2, intl)];
                case 5:
                    _b = _d.apply(_c, _e.concat([(_f.message = _g.sent(), _f)]));
                    _g.label = 6;
                case 6:
                    // CZNA-1625 — the draft's delete may have happened; see `accept`.
                    _a.apply(void 0, [_b]);
                    return [2 /*return*/];
                case 7:
                    setInFlight(false);
                    return [7 /*endfinally*/];
                case 8:
                    advance();
                    return [2 /*return*/];
            }
        });
    }); };
    var offer = queue ? queue.offers[queue.index] : null;
    var backLinkOfferModal = queue && offer ? (React.createElement(BackLinkOfferModal, { key: "".concat(offer.targetRecordId, "-").concat(queue.index), offer: offer, index: queue.index + 1, total: queue.offers.length, inFlight: inFlight, error: error, confirmDisabled: Boolean(unknownTargets[offer.targetRecordId]), onConfirm: function (designation) { return confirm(offer, designation); }, onDecline: function () { return decline(offer); } })) : null;
    return { offerBackLinks: offerBackLinks, backLinkOfferModal: backLinkOfferModal };
};
export default useBackLinkOffers;
